import type { CSSProperties } from 'react';
import React, { forwardRef } from 'react';
import classNames from 'classnames';

import './icon-button.scss';

import { useDragonfruit } from '../../contexts/DragonfruitProvider';
import type {
  IconButtonAppearance,
  IconButtonSize,
  InternalIconButtonAppearance,
} from '../../icons/component_prop_defs';
import { IconSizes } from '../../icons/component_prop_defs';
import type { UserInteractionProps } from '../../utils/user_interactions';
import Icon, { type IconProps } from '../Icon/Icon';
import ConditionalLink from '../Link/ConditionalLink';

export type OmittedIconButtonProps =
  | 'selectedBackgroundColor'
  | 'onMouseEnter'
  | 'onMouseLeave'
  | 'isSquare';

export type BadgeAppearance = 'error' | 'warning';

interface BaseIconButtonProps extends UserInteractionProps {
  className?: string;
  children?: React.ReactNode;
  size?: IconButtonSize;
  disabled?: boolean;
  selected?: boolean;
  isSquare?: boolean;
  backgroundColor?: string;
  /*
   * Background color of the button when it is selected. This does not affect the icon's color.
   */
  selectedBackgroundColor?: string;
  onClick?: (ev: React.MouseEvent) => void;
  /**
   * Link to redirect to. Prefer setting `link` over specifying an `onClick` for links because
   * `link` will support right clicking to open in a new tab.
   *
   * Relative links (determined by whether the input starts with "/") will open in the current tab.
   * Absolute links will open in a new tab by default.
   */
  link?: string;
  ariaLabel?: string;
  onMouseEnter?: (ev: React.MouseEvent) => void;
  onMouseLeave?: (ev: React.MouseEvent) => void;
  onMouseDown?: (ev: React.MouseEvent) => void;
  onMouseUp?: (ev: React.MouseEvent) => void;
  onContextMenu?: (ev: React.MouseEvent) => void;
  /* Display a red or yellow dot in the top right corner. */
  badge?: BadgeAppearance;
}

export interface InternalIconButtonProps extends BaseIconButtonProps {
  appearance: InternalIconButtonAppearance;
}

export const InternalIconButton = forwardRef<HTMLElement, InternalIconButtonProps>(
  function InternalIconButton(props, ref: React.RefObject<HTMLElement>) {
    const {
      appearance,
      isSquare,
      size,
      selected,
      disabled,
      onClick,
      link,
      className,
      children,
      ariaLabel,
      backgroundColor,
      selectedBackgroundColor,
      onMouseEnter,
      onMouseLeave,
      onMouseDown,
      onMouseUp,
      onContextMenu,
      badge,
      trackingId,
      eventData,
    } = props;
    const dragonfruit = useDragonfruit();

    const style: CSSProperties = {};
    if (!disabled && selected && selectedBackgroundColor) {
      style.backgroundColor = selectedBackgroundColor;
    } else if (backgroundColor) {
      style.backgroundColor = backgroundColor;
    }
    return (
      <ConditionalLink link={link} disabled={disabled}>
        <button
          data-testid="dragonfruit-icon-button"
          ref={ref as any}
          className={classNames(
            'icon-button',
            `icon-button-appearance-${appearance}`,
            `icon-button-size-${size}`,
            `badge-${badge}`,
            className,
            {
              disabled,
              selected: selected && !disabled,
              'is-square': !!isSquare,
            },
          )}
          role="button"
          // We can't set disabled because it messes with mouseleave event handling, so just don't set a callback
          // if the button is disabled.
          onClick={(e) => {
            if (disabled) {
              return;
            }
            onClick?.(e);
            dragonfruit?.trackUserInteraction?.({
              componentInteractionId: 'dragonfruit-icon-button-click',
              trackingId,
              eventData: {
                ariaLabel,
                ...eventData,
              },
            });
          }}
          aria-label={ariaLabel}
          style={style}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          onMouseDown={onMouseDown}
          onMouseUp={onMouseUp}
          onContextMenu={(e) => {
            if (disabled) {
              return;
            }
            onContextMenu?.(e);
          }}
        >
          {children}
        </button>
      </ConditionalLink>
    );
  },
);

export interface IconButtonProps extends Omit<BaseIconButtonProps, OmittedIconButtonProps> {
  // TODO: Make this the stricter DragonFruitIcon type
  icon: IconProps['src'];
  appearance?: IconButtonAppearance;
  enabledColor?: string;
  disabledColor?: string;
}

const IconButton: React.FC<IconButtonProps> = (props) => {
  const {
    icon,
    appearance = 'default',
    enabledColor,
    disabledColor,
    selected,
    disabled,
    size = 'medium',
    ...otherProps
  } = props;

  return (
    <InternalIconButton
      appearance={appearance}
      selected={selected}
      disabled={disabled}
      size={size}
      {...otherProps}
    >
      <Icon src={icon} height={IconSizes[size]} color={disabled ? disabledColor : enabledColor} />
    </InternalIconButton>
  );
};

export default IconButton;
